const t = key => I18n.t(`webshop.blog_images.${key}`);
export const alignments = ['left', 'center', 'right'];

export function alignmentButtons(value, change) {
  const group = document.createElement('div'); group.className = 'btn-group';
  group.setAttribute('role', 'group'); group.setAttribute('aria-label', t('alignment'));
  alignments.forEach(alignment => {
    const button = document.createElement('button'); button.type = 'button';
    button.className = 'btn btn-outline-secondary btn-sm'; button.dataset.blogImageAlign = alignment;
    button.title = t(`align_${alignment}`); button.setAttribute('aria-label', button.title);
    button.setAttribute('aria-pressed', String(alignment === value));
    const x = alignment === 'left' ? 3 : alignment === 'right' ? 13 : 8;
    button.innerHTML = `<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M3 3h18M3 21h18" stroke="currentColor" stroke-width="2"/><rect x="${x}" y="7" width="8" height="10" rx="1" fill="currentColor"/></svg>`;
    button.addEventListener('mousedown', event => event.preventDefault());
    button.addEventListener('click', () => {
      group.querySelectorAll('button').forEach(item => item.setAttribute('aria-pressed', String(item === button)));
      change(alignment);
    });
    group.append(button);
  });
  return group;
}

// Controls live outside Trix, so handles and toolbars never enter the saved article.
export default function imageControls(editor, {details, replace, edit}) {
  let attachment, dragging, frame;
  const overlay = document.createElement('div'); overlay.className = 'blog-image-selection'; overlay.hidden = true;
  // Let keyboard users focus these external controls inside Bootstrap's blog modal.
  overlay.addEventListener('focusin', event => event.stopPropagation());
  const toolbar = document.createElement('div'); toolbar.className = 'blog-image-selection-toolbar';
  const align = alignmentButtons('center', alignment => {
    if (attachment && details(attachment).alignment !== alignment) attachment = replace(attachment, {...details(attachment), alignment});
    position();
  });
  const modify = document.createElement('button'); modify.type = 'button';
  modify.className = 'btn btn-outline-secondary btn-sm'; modify.textContent = t('edit');
  modify.addEventListener('mousedown', event => event.preventDefault());
  modify.addEventListener('click', () => { const current = attachment; hide(); edit(current); });
  toolbar.append(align, modify); overlay.append(toolbar);
  const ghost = document.createElement('img'); ghost.className = 'blog-image-resize-preview'; ghost.hidden = true; ghost.alt = '';
  overlay.append(ghost);
  const image = () => attachment && editor.querySelector(`[data-trix-id="${attachment.id}"] img`);
  const maxWidth = () => {
    const style = getComputedStyle(editor);
    return Math.max(1, Math.min(10000, editor.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight)));
  };
  const clamp = width => Math.max(1, Math.min(Math.floor(maxWidth()), Math.round(width)));
  const position = () => {
    cancelAnimationFrame(frame);
    const target = image();
    if (!target || !target.getClientRects().length || !editor.isConnected) { hide(); return; }
    const rect = target.getBoundingClientRect();
    overlay.hidden = false;
    overlay.style.cssText = `left:${rect.left}px;top:${rect.top}px;width:${rect.width}px;height:${rect.height}px`;
    // Keep the toolbar reachable even when a small image is aligned to the right.
    toolbar.style.left = `${Math.max(12 - rect.left, Math.min(0, window.innerWidth - rect.left - toolbar.offsetWidth - 12))}px`;
    const mainToolbar = editor.toolbarElement?.getBoundingClientRect();
    const above = rect.top - toolbar.offsetHeight - 8;
    const overlapsToolbar = mainToolbar && above < mainToolbar.bottom && rect.top > mainToolbar.top;
    // Do not cover Trix's Undo/Redo buttons when an image starts the article.
    const below = above < 8 || overlapsToolbar;
    toolbar.style.top = below ? (rect.bottom + toolbar.offsetHeight + 8 <= window.innerHeight ? 'calc(100% + 8px)' : '8px') : '';
    toolbar.style.bottom = below ? 'auto' : '';
    const value = details(attachment)?.alignment || 'center';
    align.querySelectorAll('button').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.blogImageAlign === value)));
    overlay.querySelectorAll('[data-blog-image-resize]').forEach(handle => handle.title = `${t('resize')}: ${Math.round(rect.width)} px`);
  };
  const hide = () => { overlay.hidden = true; attachment = null; };
  const schedule = () => { if (!dragging && attachment) { cancelAnimationFrame(frame); frame = requestAnimationFrame(position); } };
  ['top_left', 'top_right', 'bottom_left', 'bottom_right'].forEach(corner => {
    const handle = document.createElement('button'); handle.type = 'button'; handle.dataset.blogImageResize = corner;
    handle.className = `blog-image-resize-handle ${corner}`;
    handle.setAttribute('aria-label', `${t('resize')} (${t(corner)})`);
    handle.addEventListener('pointerdown', event => {
      if (event.button !== 0 || !attachment) return;
      event.preventDefault();
      const target = image(); const rect = target.getBoundingClientRect();
      dragging = {attachment, x: event.clientX, y: event.clientY, width: rect.width, ratio: rect.width / rect.height, corner, value: rect.width};
      ghost.src = target.currentSrc || target.src; ghost.hidden = false;
      handle.setPointerCapture(event.pointerId);
    });
    handle.addEventListener('pointermove', event => {
      if (!dragging) return;
      const dx = (event.clientX - dragging.x) * (corner.endsWith('left') ? -1 : 1);
      const dy = (event.clientY - dragging.y) * (corner.startsWith('top') ? -1 : 1) * dragging.ratio;
      const delta = Math.abs(dx) >= Math.abs(dy) ? dx : dy;
      dragging.value = clamp(dragging.width + delta);
      overlay.style.width = `${dragging.value}px`; overlay.style.height = `${dragging.value / dragging.ratio}px`;
    });
    const finish = event => {
      if (!dragging) return;
      const current = dragging; dragging = null; ghost.hidden = true;
      if (event.type === 'pointerup' && current.value !== current.width) {
        attachment = replace(current.attachment, {...details(current.attachment), width: current.value});
      }
      if (handle.hasPointerCapture(event.pointerId)) handle.releasePointerCapture(event.pointerId);
      position();
    };
    handle.addEventListener('pointerup', finish); handle.addEventListener('pointercancel', finish);
    handle.addEventListener('keydown', event => {
      if (!attachment || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
      event.preventDefault();
      const direction = ['ArrowLeft', 'ArrowDown'].includes(event.key) ? -1 : 1;
      attachment = replace(attachment, {...details(attachment), width: clamp(image().getBoundingClientRect().width + direction * (event.shiftKey ? 10 : 1))});
      position(); handle.focus();
    });
    overlay.append(handle);
  });
  document.body.append(overlay);
  const select = event => {
    const target = event.target.closest('img');
    if (!target || !editor.contains(target)) { hide(); return; }
    const figure = target.closest('[data-trix-id]');
    const selected = figure && editor.editor.getDocument().getAttachmentById(Number(figure.dataset.trixId));
    if (!selected || !details(selected)) return;
    // Suppress native attachment editing and linked-image navigation inside the editor.
    event.preventDefault(); event.stopPropagation();
    attachment = selected; position();
  };
  editor.addEventListener('mousedown', select, true);
  editor.addEventListener('click', select, true);
  const outside = event => { if (!editor.contains(event.target) && !overlay.contains(event.target) && !dragging) hide(); };
  const escape = event => { if (event.key === 'Escape' && attachment) { hide(); editor.focus(); } };
  document.addEventListener('pointerdown', outside);
  document.addEventListener('keydown', escape);
  document.addEventListener('scroll', schedule, true);
  window.addEventListener('resize', schedule);
  editor.addEventListener('trix-render', schedule);
  editor.addEventListener('trix-change', schedule);
  const observer = new MutationObserver(() => {
    if (editor.isConnected) return;
    cancelAnimationFrame(frame); overlay.remove(); observer.disconnect();
    document.removeEventListener('pointerdown', outside); document.removeEventListener('keydown', escape);
    document.removeEventListener('scroll', schedule, true); window.removeEventListener('resize', schedule);
  });
  observer.observe(document.body, {childList: true, subtree: true});
}
